import {
	AIFileProcessingStatus,
	type AIFileProcessingStatusValues,
	AIFileProcessingType,
	type AIFileProcessingTypeValues,
	FileWithMetadataPayload,
} from "@dune/types";
import { formatDateTime } from "@dune/utils";

const getStatusColor = (status: AIFileProcessingStatusValues) => {
	switch (status) {
		case AIFileProcessingStatus.PENDING:
			return "bg-yellow-100 text-yellow-800";
		case AIFileProcessingStatus.RUNNING:
			return "bg-blue-100 text-blue-800";
		case AIFileProcessingStatus.COMPLETED:
			return "bg-green-100 text-green-800";
		case AIFileProcessingStatus.FAILED:
			return "bg-red-100 text-red-800";
		default:
			return "bg-gray-100 text-gray-800";
	}
};

const getStatusIcon = (status: AIFileProcessingStatusValues) => {
	switch (status) {
		case AIFileProcessingStatus.PENDING:
			return "⏳";
		case AIFileProcessingStatus.RUNNING:
			return "🔄";
		case AIFileProcessingStatus.COMPLETED:
			return "✅";
		case AIFileProcessingStatus.FAILED:
			return "❌";
		default:
			return "❓";
	}
};

const getTypeLabel = (type: AIFileProcessingTypeValues) => {
	switch (type) {
		case AIFileProcessingType.CLASSIFICATION:
			return "Classification";
		case AIFileProcessingType.EXTRACTION:
			return "Extraction";
		default:
			return type;
	}
};

export const ProcessingJobsDetails = ({
	job,
}: {
	job: FileWithMetadataPayload["processingJobs"][number];
}) => {
	return (
		<div
			key={job.id}
			className="flex flex-col gap-2 text-xs p-2 bg-gray-50 rounded"
		>
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<span className="font-medium">{getTypeLabel(job.type)}</span>
					<span className="text-gray-500">•</span>
					<span className="text-gray-600">
						Créé le {formatDateTime(job.createdAt)}
					</span>
				</div>
				<div className="flex items-center gap-1">
					<span
						className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}
					>
						<span>{getStatusIcon(job.status)}</span>
						{job.status}
					</span>
				</div>
			</div>

			<div className="flex flex-col gap-1 text-xs text-gray-600">
				{job.startedAt ? (
					<div className="flex items-center gap-2">
						<span className="text-green-600">🚀 Démarré :</span>
						<span>{formatDateTime(job.startedAt)}</span>
					</div>
				) : (
					<div className="flex items-center gap-2">
						<span className="text-orange-600">⏸️ En attente de démarrage</span>
					</div>
				)}

				{job.completedAt && (
					<div className="flex items-center gap-2">
						<span className="text-blue-600">🏁 Terminé :</span>
						<span>{formatDateTime(job.completedAt)}</span>
					</div>
				)}
			</div>
		</div>
	);
};

export const CompactProcessingJobsDetails = ({
	jobs,
}: {
	jobs: FileWithMetadataPayload["processingJobs"];
}) => {
	const hasStarted = jobs.some((job) => job.status !== "PENDING");
	const allCompleted = jobs.every((job) => job.status === "COMPLETED");
	const hasRunning = jobs.some((job) => job.status === "RUNNING");
	const hasFailed = jobs.some((job) => job.status === "FAILED");

	if (!hasStarted) {
		return (
			<div className="text-xs text-gray-500 italic">
				<span className="text-gray-600">Aucun traitement démarré</span>
			</div>
		);
	}

	if (hasFailed) {
		return (
			<div className="text-xs text-gray-500 italic">
				<span className="text-red-600">Traitement échoué</span>
			</div>
		);
	}

	if (allCompleted) {
		return (
			<div className="text-xs text-gray-500 italic">
				<span className="text-green-600">Traitement terminé</span>
			</div>
		);
	}

	if (hasRunning) {
		return (
			<div className="text-xs text-gray-500 italic">
				<span className="text-yellow-600">Traitement en cours</span>
			</div>
		);
	}

	return (
		<div className="text-xs text-gray-500 italic">
			<span className="text-orange-600">Traitement en attente</span>
		</div>
	);
};
